import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';

// ── Config ────────────────────────────────────────────────────────────────────

export interface BlobSpec {
  sizeScale: number; // larger = stiffer (shorter transition) = leads the pack
}

export interface MetaballConfig {
  contrast: number;
  color:    string;
  speed:    number; // multiplier on transition duration (lower = faster)
  blobs:    BlobSpec[];
}

export const NAV_DEFAULT: MetaballConfig = {
  contrast: 22, color: '#e879f9', speed: 0.3,
  blobs: [
    { sizeScale: 1.00 },
    { sizeScale: 0.70 },
    { sizeScale: 0.35 },
  ],
};

export const GRID_DEFAULT: MetaballConfig = {
  contrast: 22, color: '#2dd4bf', speed: 0.3,
  blobs: [
    { sizeScale: 1.00 },
    { sizeScale: 0.70 },
    { sizeScale: 0.35 },
  ],
};

// ── Derived per-blob values ───────────────────────────────────────────────────
//
// Larger blob → shorter duration → arrives first → leads.
// Smaller blob → longer duration → lags behind → creates neck.
// No transition-delay — the lag comes purely from different durations.

function transitionDuration(sizeScale: number, speed: number): number {
  // range: sizeScale 1.0 → 0.22s,  sizeScale 0.35 → 0.44s
  return ((0.5 - sizeScale * 0.28) / speed);
}

function deriveBlur(sizeScale: number, isNav: boolean): number {
  return Math.max(3, (isNav ? 14 : 20) * sizeScale);
}

// ── Wobble keyframes ──────────────────────────────────────────────────────────
//
// Three independent border-radius animations with slightly different shapes
// and durations — assigned round-robin to blobs so they're never in sync.

// Each keyframe encodes: translate(-50% + wanderX, -50% + wanderY) + scale pulse + rotate.
// Wander offsets keep each blob off-center relative to the target so the combined
// shape is lumpy rather than a symmetric squish. Scale pulses make blobs breathe.
const wobble0 = keyframes`
  0%   { transform: translate(calc(-50% +  6px), calc(-50% -  4px)) scale(1.0)  rotate(-5deg);  }
  25%  { transform: translate(calc(-50% - 10px), calc(-50% +  8px)) scale(1.18) rotate(4deg);   }
  50%  { transform: translate(calc(-50% +  4px), calc(-50% + 12px)) scale(0.88) rotate(10deg);  }
  75%  { transform: translate(calc(-50% + 12px), calc(-50% -  6px)) scale(1.12) rotate(-8deg);  }
  100% { transform: translate(calc(-50% +  6px), calc(-50% -  4px)) scale(1.0)  rotate(-5deg);  }
`;
const wobble1 = keyframes`
  0%   { transform: translate(calc(-50% - 12px), calc(-50% +  5px)) scale(1.15) rotate(8deg);   }
  33%  { transform: translate(calc(-50% +  8px), calc(-50% - 10px)) scale(0.85) rotate(-6deg);  }
  66%  { transform: translate(calc(-50% -  5px), calc(-50% + 14px)) scale(1.1)  rotate(12deg);  }
  100% { transform: translate(calc(-50% - 12px), calc(-50% +  5px)) scale(1.15) rotate(8deg);   }
`;
const wobble2 = keyframes`
  0%   { transform: translate(calc(-50% +  9px), calc(-50% + 10px)) scale(0.9)  rotate(6deg);   }
  40%  { transform: translate(calc(-50% - 14px), calc(-50% -  8px)) scale(1.2)  rotate(-10deg); }
  80%  { transform: translate(calc(-50% +  5px), calc(-50% - 13px)) scale(0.95) rotate(7deg);   }
  100% { transform: translate(calc(-50% +  9px), calc(-50% + 10px)) scale(0.9)  rotate(6deg);   }
`;

const WOBBLES  = [wobble0, wobble1, wobble2];
// Slightly different durations + negative delays = each blob starts mid-animation
const DURATIONS = [3.8, 4.4, 3.2]; // seconds
const OFFSETS   = [-1.2, -2.7, -0.5]; // animation-delay (negative = start mid-cycle)

function wobbleAnimation(i: number, sizeScale: number) {
  const idx = i % 3;
  // Smaller blobs wobble faster (more organic)
  const dur = DURATIONS[idx] * (0.5 + sizeScale * 0.5);
  return css`
    animation: ${WOBBLES[idx]} ${dur}s ${OFFSETS[idx]}s infinite linear;
  `;
}

// ── BlobSwarm ─────────────────────────────────────────────────────────────────
//
// No rAF. Each blob is a plain div with:
//   • CSS transition on left/top — duration derived from sizeScale
//   • CSS animation on border-radius — the wobble keyframes above
// React only writes new left/top values when the selected item changes.

const BlobEl = styled.div<{
  $color:    string;
  $w:        number;
  $h:        number;
  $blur:     number;
  $x:        number;
  $y:        number;
  $duration: number;
  $idx:      number;
  $scale:    number;
}>`
  position: absolute;
  background: ${p => p.$color};
  width:  ${p => p.$w}px;
  height: ${p => p.$h}px;
  border-radius: 50%;
  filter: blur(${p => p.$blur}px);
  left:   ${p => p.$x}px;
  top:    ${p => p.$y}px;
  will-change: left, top, transform;
  transition:
    left ${p => p.$duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    top  ${p => p.$duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  ${p => wobbleAnimation(p.$idx, p.$scale)}
`;

interface BlobSwarmProps {
  config:  MetaballConfig;
  targetX: number;
  targetY: number;
  baseW:   number;
  baseH:   number;
  isNav:   boolean;
}

function BlobSwarm({ config, targetX, targetY, baseW, baseH, isNav }: BlobSwarmProps) {
  return (
    <>
      {config.blobs.map((spec, i) => (
        <BlobEl
          key={i}
          $color={config.color}
          $w={baseW * spec.sizeScale}
          $h={baseH * spec.sizeScale}
          $blur={deriveBlur(spec.sizeScale, isNav)}
          $x={targetX}
          $y={targetY}
          $duration={transitionDuration(spec.sizeScale, config.speed)}
          $idx={i}
          $scale={spec.sizeScale}
        />
      ))}
    </>
  );
}

// ── Config panel ──────────────────────────────────────────────────────────────

const Panel = styled.div`
  background: #0d0d0d;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: 'JetBrains Mono', monospace;
  min-width: 0;
`;
const PanelHdr = styled.div`display: flex; align-items: center; justify-content: space-between; margin-bottom: 2px;`;
const PanelTitle = styled.span`font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; opacity: 0.3; color: #fff;`;
const SRow = styled.div`display: grid; grid-template-columns: 52px 1fr 32px; align-items: center; gap: 8px;`;
const SLbl = styled.span`font-size: 10px; letter-spacing: 0.05em; text-transform: uppercase; opacity: 0.4; color: #fff;`;
const SVal = styled.span`font-size: 10px; opacity: 0.55; text-align: right; font-variant-numeric: tabular-nums; color: #fff;`;
const SInput = styled.input.attrs({ type: 'range' })`
  width: 100%; height: 2px; appearance: none;
  background: rgba(255,255,255,0.14); border-radius: 2px; outline: none; cursor: pointer;
  &::-webkit-slider-thumb { appearance: none; width: 11px; height: 11px; border-radius: 50%; background: rgba(255,255,255,0.8); cursor: pointer; }
  &::-moz-range-thumb { width: 11px; height: 11px; border: none; border-radius: 50%; background: rgba(255,255,255,0.8); cursor: pointer; }
`;
interface SliderProps { label: string; value: number; min: number; max: number; step: number; dec?: number; onChange: (v: number) => void; }
const Slider = ({ label, value, min, max, step, dec = 2, onChange }: SliderProps) => (
  <SRow>
    <SLbl>{label}</SLbl>
    <SInput min={min} max={max} step={step} value={value} onChange={e => onChange(parseFloat(e.target.value))} />
    <SVal>{value.toFixed(dec)}</SVal>
  </SRow>
);
const Divider = styled.div`height: 1px; background: rgba(255,255,255,0.06); margin: 2px 0;`;
const BlobCard = styled.div`border: 1px solid rgba(255,255,255,0.07); border-radius: 7px; padding: 10px 12px; display: flex; flex-direction: column; gap: 8px;`;
const BlobHdr = styled.div`display: flex; align-items: center; justify-content: space-between;`;
const BlobIdx = styled.span`font-size: 10px; opacity: 0.25; color: #fff;`;
const Row = styled.div`display: flex; align-items: center; gap: 8px;`;
const Btn = styled.button`
  background: none; border: 1px solid rgba(255,255,255,0.1); border-radius: 5px; cursor: pointer;
  font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase;
  color: rgba(255,255,255,0.4); padding: 4px 9px;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: rgba(255,255,255,0.28); color: rgba(255,255,255,0.8); }
`;
const IconBtn = styled.button`
  background: none; border: none; cursor: pointer; opacity: 0.25; font-size: 15px;
  line-height: 1; color: #fff; padding: 0; transition: opacity 0.15s;
  &:hover { opacity: 0.7; }
`;
const ColorInput = styled.input.attrs({ type: 'color' })`
  width: 22px; height: 16px; border: none; border-radius: 3px; padding: 0; cursor: pointer; background: none;
  &::-webkit-color-swatch-wrapper { padding: 0; }
  &::-webkit-color-swatch { border: 1px solid rgba(255,255,255,0.15); border-radius: 3px; }
`;

interface ConfigPanelProps { config: MetaballConfig; onChange: (c: MetaballConfig) => void; onReset: () => void; }
function ConfigPanel({ config, onChange, onReset }: ConfigPanelProps) {
  const set = <K extends keyof MetaballConfig>(key: K, val: MetaballConfig[K]) =>
    onChange({ ...config, [key]: val });
  const setBlob = (i: number, val: number) =>
    onChange({ ...config, blobs: config.blobs.map((b, idx) => idx === i ? { ...b, sizeScale: val } : b) });
  const addBlob = () => {
    const last = config.blobs[config.blobs.length - 1];
    onChange({ ...config, blobs: [...config.blobs, { sizeScale: +Math.max(0.12, last.sizeScale * 0.5).toFixed(2) }] });
  };
  return (
    <Panel>
      <PanelHdr>
        <PanelTitle>config</PanelTitle>
        <Btn onClick={onReset}>reset</Btn>
      </PanelHdr>
      <Row>
        <SLbl style={{ flexShrink: 0 }}>color</SLbl>
        <ColorInput value={config.color} onChange={e => set('color', e.target.value)} />
      </Row>
      <Slider label="contrast" value={config.contrast} min={5}   max={50}  step={1}   dec={0} onChange={v => set('contrast', v)} />
      <Slider label="speed"    value={config.speed}    min={0.1} max={1.0} step={0.1} dec={1} onChange={v => set('speed',    v)} />
      <Divider />
      {config.blobs.map((blob, i) => (
        <BlobCard key={i}>
          <BlobHdr>
            <BlobIdx>blob {i}</BlobIdx>
            {config.blobs.length > 1 && <IconBtn onClick={() => onChange({ ...config, blobs: config.blobs.filter((_, j) => j !== i) })}>×</IconBtn>}
          </BlobHdr>
          <Slider label="size" value={blob.sizeScale} min={0.1} max={1.5} step={0.05} onChange={v => setBlob(i, v)} />
        </BlobCard>
      ))}
      <Btn onClick={addBlob} style={{ alignSelf: 'flex-end' }}>+ blob</Btn>
    </Panel>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────

const DemoWrap = styled.div`display: flex; flex-direction: column; gap: 52px;`;
const DemoRow  = styled.div`
  display: grid; grid-template-columns: 1fr 200px; gap: 16px; align-items: start;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const DemoCol  = styled.div`display: flex; flex-direction: column; gap: 12px;`;
const Label    = styled.p`
  margin: 0; font-family: 'JetBrains Mono', monospace; font-size: 11px;
  letter-spacing: 0.1em; text-transform: uppercase; color: var(--text); opacity: 0.4;
`;

// ── Nav ───────────────────────────────────────────────────────────────────────

const NAV_ITEMS = ['about', 'work', 'projects', 'skills', 'contact'];
const NavWrapper   = styled.div`position: relative; background: #0d0d0d; border-radius: 12px; overflow: hidden;`;
const NavBlobLayer = styled.div`position: absolute; inset: 0; pointer-events: none; background: #0d0d0d;`;
const NavTextLayer = styled.div`position: relative; z-index: 2; display: flex; width: 100%;`;
const NavBtn = styled.button<{ $sel: boolean }>`
  flex: 1; background: none; border: none; cursor: pointer;
  font-family: 'JetBrains Mono', monospace; font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase;
  font-weight: ${p => p.$sel ? 700 : 400}; color: rgba(255,255,255,${p => p.$sel ? 1 : 0.5});
  padding: 15px 8px; transition: color 0.25s, font-weight 0.25s;
`;

function MetaballNav({ config }: { config: MetaballConfig }) {
  const [sel, setSel] = useState(2);
  const selRef  = useRef(sel);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [target, setTarget] = useState({ x: 0, y: 0, w: 0, h: 0 });

  const recalc = useCallback((idx?: number) => {
    const i = idx ?? selRef.current;
    const wrap = wrapRef.current; const el = btnRefs.current[i];
    if (!wrap || !el) return;
    const wr = wrap.getBoundingClientRect(); const r = el.getBoundingClientRect();
    setTarget({ x: r.left - wr.left + r.width / 2, y: r.top - wr.top + r.height / 2, w: r.width * 0.82, h: r.height * 0.9 });
  }, []);

  useLayoutEffect(() => { recalc(); const h = () => recalc(); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, [recalc]);
  const pick = (i: number) => { setSel(i); selRef.current = i; recalc(i); };

  return (
    <NavWrapper ref={wrapRef}>
      <NavBlobLayer style={{ filter: `contrast(${config.contrast})` }}>
        <BlobSwarm config={config} targetX={target.x} targetY={target.y} baseW={target.w} baseH={target.h} isNav />
      </NavBlobLayer>
      <NavTextLayer>
        {NAV_ITEMS.map((item, i) => (
          <NavBtn key={item} ref={el => { btnRefs.current[i] = el; }} $sel={i === sel} onClick={() => pick(i)}>{item}</NavBtn>
        ))}
      </NavTextLayer>
    </NavWrapper>
  );
}

// ── Grid ──────────────────────────────────────────────────────────────────────

const GRID_ITEMS = ['render', 'physics', 'audio', 'network', 'ai', 'tools', 'shaders', 'data', 'systems'];
const GridWrapper   = styled.div`position: relative; background: #0d0d0d; border-radius: 12px; overflow: hidden; width: 100%;`;
const GridBlobLayer = styled.div`position: absolute; inset: 0; pointer-events: none; background: #0d0d0d;`;
const GridTextLayer = styled.div`position: relative; z-index: 2; display: grid; grid-template-columns: repeat(3, 1fr);`;
const GridBtn = styled.button<{ $sel: boolean }>`
  background: none; border: 1px solid rgba(255,255,255,0.06); cursor: pointer;
  font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
  font-weight: ${p => p.$sel ? 700 : 400}; color: rgba(255,255,255,${p => p.$sel ? 1 : 0.4});
  aspect-ratio: 2.5 / 1; display: flex; align-items: center; justify-content: center;
  transition: color 0.25s, font-weight 0.25s;
`;

function MetaballGrid({ config }: { config: MetaballConfig }) {
  const [sel, setSel] = useState(4);
  const selRef   = useRef(sel);
  const wrapRef  = useRef<HTMLDivElement>(null);
  const cellRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [target, setTarget] = useState({ x: 0, y: 0, r: 0 });

  const recalc = useCallback((idx?: number) => {
    const i = idx ?? selRef.current;
    const wrap = wrapRef.current; const el = cellRefs.current[i];
    if (!wrap || !el) return;
    const wr = wrap.getBoundingClientRect(); const r = el.getBoundingClientRect();
    setTarget({ x: r.left - wr.left + r.width / 2, y: r.top - wr.top + r.height / 2, r: Math.min(r.width, r.height) * 0.38 });
  }, []);

  useLayoutEffect(() => { recalc(); const h = () => recalc(); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, [recalc]);
  const pick = (i: number) => { setSel(i); selRef.current = i; recalc(i); };
  const d = target.r * 2;

  return (
    <GridWrapper ref={wrapRef}>
      <GridBlobLayer style={{ filter: `contrast(${config.contrast})` }}>
        <BlobSwarm config={config} targetX={target.x} targetY={target.y} baseW={d} baseH={d} isNav={false} />
      </GridBlobLayer>
      <GridTextLayer>
        {GRID_ITEMS.map((item, i) => (
          <GridBtn key={item} ref={el => { cellRefs.current[i] = el; }} $sel={i === sel} onClick={() => pick(i)}>{item}</GridBtn>
        ))}
      </GridTextLayer>
    </GridWrapper>
  );
}

// ── Export ────────────────────────────────────────────────────────────────────

const MetaballDemo: React.FC = () => {
  const [navCfg,  setNavCfg]  = useState<MetaballConfig>(NAV_DEFAULT);
  const [gridCfg, setGridCfg] = useState<MetaballConfig>(GRID_DEFAULT);
  return (
    <DemoWrap>
      <DemoRow>
        <DemoCol><Label>nav bar</Label><MetaballNav config={navCfg} /></DemoCol>
        <ConfigPanel config={navCfg} onChange={setNavCfg} onReset={() => setNavCfg(NAV_DEFAULT)} />
      </DemoRow>
      <DemoRow>
        <DemoCol><Label>grid</Label><MetaballGrid config={gridCfg} /></DemoCol>
        <ConfigPanel config={gridCfg} onChange={setGridCfg} onReset={() => setGridCfg(GRID_DEFAULT)} />
      </DemoRow>
    </DemoWrap>
  );
};

export default MetaballDemo;
